import React, { useEffect, useState } from 'react';
import { Input, Table, Button, Stack, IconButton, Grid, Col, Row, Divider, SortType } from 'rsuite';
import { BaseErrorModel, LatLng, PagedResponseModel, PaginationFilterResult } from '@/models/core.model';
import EditIcon from '@rsuite/icons/Edit';
import { AxiosError } from 'axios';
import { toastErrorMessage } from '@/utils/toasterMessage';
import TablePagination from '@/components/TablePagination';
import ImageCell from '@/components/ImageCell';
import { useNavigate } from 'react-router-dom';

import { DiscoveryPoint, DiscoveryPointPaginationFilterRequest } from '@/models/discovery-point.model';
import { getPagedDiscoveryPoints } from '@/services/discovery-point.service';
import PageContent from '@/components/PageContent';
import MapMultipleSelector, { MarkerInfo } from '@/components/MapMultipleSelector';
import { getHeight } from 'rsuite/esm/DOMHelper';

const { Column, HeaderCell, Cell } = Table;

interface CityDiscoveryPointsProps {
  cityId: number;
  mapCenter?: LatLng;
}
const CityDiscoveryPoints : React.FC<CityDiscoveryPointsProps> = ({cityId, mapCenter}) => {
  const [filterReq, setFilterReq] = useState<DiscoveryPointPaginationFilterRequest>(new DiscoveryPointPaginationFilterRequest());
  const [pageRes, setPageRes] = useState<PaginationFilterResult>(new PaginationFilterResult());
  const [items, setItems] = useState<DiscoveryPoint[]>([]);
  const [loading, setLoading] = useState(true);
  const nav = useNavigate();

  useEffect(() => {
    fetchData(filterReq);
  }, [filterReq]);

  const fetchData = async (req: DiscoveryPointPaginationFilterRequest) => {
    setLoading(true);
    try {
      const finalReq = {
        ...req,
        cityId: cityId,
      };   
      const result: PagedResponseModel<DiscoveryPoint> = await getPagedDiscoveryPoints(finalReq);
      if (result.success) {
        setItems(result.result);
        setPageRes(pre => ({ 
          ...pre, 
          pageNumber: result.pageNumber, 
          totalRecords: result.totalRecords, 
          pageSize: result.pageSize 
        }));
      }
    } catch (err) {
      setItems([]);
      const errorModel = (err as AxiosError<BaseErrorModel>).response?.data;
      toastErrorMessage(`Error in fetching Discovery Points:${errorModel?.errorMessage}`);
    }
    finally {
      setLoading(false);
    }
  };

  const handleSortColumn = (sortColumn: string, sortType?: SortType) => {
    setFilterReq(pre => ({
      ...pre, 
      orderBy: sortColumn,
      isAscending: sortType == 'asc'
    }));
  };

  const getMapValues = (): MarkerInfo[] => {
    return items.map((item: DiscoveryPoint) => ({
      key: item.id.toString(),
      radius: Number(item.radius),
      tooltip: item.name,
      position: {
        lat: Number(item.lat),
        lng: Number(item.lon),
      },
    }));
  };

  const handleMarkerClick = (key: string) => {
    nav(`/discovery-points/${key}`, {state: {cityId}});
  };

  return (
    <PageContent>
      <Stack className="table-toolbar" justifyContent="space-between">
        <Button appearance="primary" onClick={() => nav(`/discovery-points/new`, {state: {cityId}})}>
          Add Discovery Point
        </Button>

        <Stack spacing={6}>   
          <Input 
            placeholder="Keyword" 
            value={filterReq.keyword ?? ''} 
            onChange={dataKey => setFilterReq(pre => ({ ...pre, keyword: dataKey }))} 
          />    
        </Stack>
      </Stack>      
      <Grid w='100%' fluid>
        <Row>
          <Col span={{xs:24, xl:8}}>
            <Table
              height={Math.max(getHeight(window) - 350, 400)}
              data={items}
              sortColumn={filterReq.orderBy}
              sortType={filterReq.isAscending ? 'asc' : 'desc'}
              onSortColumn={handleSortColumn}
              loading={loading}
            >
              <Column flexGrow={0.5} align="center" >
                <HeaderCell>Icon</HeaderCell>
                <ImageCell dataKey="imageUrl" />
              </Column>              
              <Column flexGrow={2} align="left" fullText sortable>
                <HeaderCell>Name</HeaderCell>
                <Cell dataKey="name" />
              </Column>
              <Column flexGrow={0.5} align="left" fullText sortable>
                <HeaderCell>Radius</HeaderCell>
                <Cell dataKey="radius" />
              </Column>
              <Column flexGrow={0.5} fixed="right">
                <HeaderCell>...</HeaderCell>

                <Cell style={{ padding: '6px' }}>
                  {(rowData : DiscoveryPoint) => (              
                    <IconButton onClick={() => nav(`/discovery-points/${rowData.id}`, {state: {cityId}})} icon={<EditIcon />} circle size="sm" />
                  )}
                </Cell>
              </Column>

            </Table>
            <Divider />
            <TablePagination 
              totalRecords={pageRes.totalRecords}
              pageSize={pageRes.pageSize}
              activePage={pageRes.pageNumber}
              onChangePage={dataKey => setFilterReq(pre => ({...pre, pageNumber: dataKey}))}
              onChangeLimit={dataKey => setFilterReq(pre => ({...pre, pageSize: dataKey}))}
            />          
          </Col>
          <Col span={{xs:24, xl:16}}>
            <MapMultipleSelector
              value={getMapValues()}
              onMarkerClick={handleMarkerClick}
              height= {`${Math.max(getHeight(window) - 270, 700)}px`}
              defaultZoom={13}
              defaultCenter={mapCenter}
            />              
          </Col>
        </Row>
      </Grid>
    </PageContent>

  );
};

export default CityDiscoveryPoints;
